import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr, buildWaLink, openWa, formatApiErrorDetail } from "@/lib/api";
import { WaAppToggle } from "@/components/admin/WaAppToggle";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Send, Plus, Pencil, Trash2, MessageCircle } from "lucide-react";

function fill(body, b) {
  return (body || "")
    .replaceAll("[Customer Name]", b.name || "")
    .replaceAll("[Trip Name]", b.trip_name || "")
    .replaceAll("[Departure Date]", b.departure_date || "")
    .replaceAll("[Booking ID]", b.booking_id || "")
    .replaceAll("[Pickup Point]", b.pickup_point || "")
    .replaceAll("[Number of Travellers]", String(b.travellers || ""))
    .replaceAll("[Amount Paid]", String(b.amount_paid || b.advance_amount || ""));
}

export default function Messages() {
  const { settings } = useSettings();
  const [bookings, setBookings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [sel, setSel] = useState([]);
  const [tplId, setTplId] = useState("");
  const [editBody, setEditBody] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = () => {
    api.get("/admin/bookings?status=Confirmed").then(({ data }) => setBookings(data));
    api.get("/admin/templates").then(({ data }) => { setTemplates(data); if (data[0]) { setTplId(data[0].id); setEditBody(data[0].body); } });
    api.get("/admin/messages").then(({ data }) => setLogs(data));
  };
  useEffect(() => { load(); }, []);

  const onTpl = (id) => { setTplId(id); const t = templates.find((x) => x.id === id); if (t) setEditBody(t.body); };
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSel = sel.length === bookings.length && bookings.length > 0;

  const send = async () => {
    const chosen = bookings.filter((b) => sel.includes(b.id));
    if (chosen.length === 0) { toast.error("Select at least one booking"); return; }
    const business = buildWaLink("1", "x").startsWith("intent://");
    if (business && chosen.length > 1) { toast.info("WhatsApp Business opens one chat at a time — sending the first; re-select the rest after."); }
    for (const [i, b] of (business ? chosen.slice(0, 1) : chosen).entries()) {
      const msg = fill(editBody, b);
      openWa(buildWaLink(b.whatsapp, msg), i * 400);
      try { await api.post("/admin/messages", { booking_id: b.booking_id || b.id, customer_name: b.name, whatsapp: b.whatsapp, template_id: tplId, message: msg, status: "sent" }); } catch {}
    }
    toast.success(`Opened WhatsApp for ${chosen.length} customer(s)`);
    setPreviewOpen(false); load();
  };

  const previewBooking = bookings.find((b) => sel.includes(b.id));

  return (
    <div className="space-y-6">
      <div><h1 className="font-heading text-4xl font-semibold">WhatsApp Message Sender</h1><p className="text-muted-foreground">Send confirmation messages to confirmed customers. API-ready deep-link workflow.</p><WaAppToggle /></div>

      <Tabs defaultValue="send">
        <TabsList><TabsTrigger value="send" data-testid="tab-send">Send</TabsTrigger><TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger><TabsTrigger value="logs" data-testid="tab-logs">Logs</TabsTrigger></TabsList>

        <TabsContent value="send" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-2xl">Confirmed Bookings</h3>
                <Button variant="outline" size="sm" onClick={() => setSel(allSel ? [] : bookings.map((b) => b.id))} data-testid="select-all">{allSel ? "Clear" : "Select All"}</Button>
              </div>
              <div className="space-y-2 max-h-96 overflow-auto">
                {bookings.map((b) => (
                  <label key={b.id} className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/40" data-testid="msg-booking">
                    <Checkbox checked={sel.includes(b.id)} onCheckedChange={() => toggle(b.id)} />
                    <div className="flex-1"><p className="font-medium">{b.name} <span className="font-mono text-xs text-muted-foreground">{b.booking_id}</span></p><p className="text-xs text-muted-foreground">{b.trip_name} · {b.departure_date}</p></div>
                  </label>
                ))}
                {bookings.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No confirmed bookings yet.</p>}
              </div>
            </div>

            <div className="rounded-xl border bg-card p-5 space-y-3">
              <div>
                <Label>Template</Label>
                <Select value={tplId} onValueChange={onTpl}><SelectTrigger data-testid="msg-template"><SelectValue placeholder="Choose template" /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Message (editable · use [Variables])</Label><Textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={12} className="font-mono text-xs" data-testid="msg-body" /></div>
              <p className="text-xs text-muted-foreground">Variables: [Customer Name] [Trip Name] [Departure Date] [Booking ID] [Pickup Point] [Number of Travellers] [Amount Paid]</p>
              <Button className="w-full rounded-full" disabled={sel.length === 0} onClick={() => setPreviewOpen(true)} data-testid="msg-send"><Send className="h-4 w-4 mr-1" /> Send to {sel.length} customer(s)</Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="templates"><TemplatesTab templates={templates} reload={load} /></TabsContent>

        <TabsContent value="logs">
          <div className="rounded-xl border bg-card overflow-x-auto">
            <table className="w-full text-sm"><thead className="bg-muted/50 text-left"><tr><th className="p-3">Customer</th><th className="p-3">WhatsApp</th><th className="p-3">Message</th><th className="p-3">Status</th><th className="p-3">Sent</th></tr></thead>
              <tbody>{logs.map((l) => (<tr key={l.id} className="border-t"><td className="p-3 font-medium">{l.customer_name}</td><td className="p-3">{l.whatsapp}</td><td className="p-3 max-w-xs truncate text-muted-foreground">{l.message}</td><td className="p-3"><Badge variant="secondary" className="rounded-full">{l.status}</Badge></td><td className="p-3 text-xs">{l.created_at?.slice(0, 16).replace("T", " ")}</td></tr>))}
                {logs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No messages sent yet</td></tr>}</tbody></table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent data-testid="msg-preview">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Preview message</DialogTitle></DialogHeader>
          {previewBooking && <div className="rounded-lg bg-[#e5ddd5] p-4"><div className="bg-[#dcf8c6] rounded-lg p-3 text-sm whitespace-pre-line shadow">{fill(editBody, previewBooking)}</div></div>}
          <p className="text-xs text-muted-foreground">This opens WhatsApp for each of the {sel.length} selected customer(s) with their personalised message.</p>
          <Button className="w-full rounded-full bg-[#25D366] hover:bg-[#1da851] text-white" onClick={send} data-testid="msg-confirm-send"><MessageCircle className="h-4 w-4 mr-1" /> Open WhatsApp & Send</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplatesTab({ templates, reload }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", body: "", active: true });

  const openNew = () => { setEditing(null); setForm({ name: "", body: "", active: true }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, body: t.body, active: t.active }); setOpen(true); };
  const save = async () => {
    if (!form.name || !form.body) { toast.error("Name and body required"); return; }
    try {
      if (editing) await api.put(`/admin/templates/${editing.id}`, form); else await api.post("/admin/templates", form);
      toast.success("Saved"); setOpen(false); reload();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (t) => { await api.delete(`/admin/templates/${t.id}`); toast.success("Deleted"); reload(); };

  return (
    <div className="space-y-4">
      <div className="flex justify-end"><Button className="rounded-full" onClick={openNew} data-testid="add-template"><Plus className="h-4 w-4 mr-1" /> Add Template</Button></div>
      <div className="grid gap-4 sm:grid-cols-2">
        {templates.map((t) => (
          <div key={t.id} className="rounded-xl border bg-card p-5">
            <div className="flex items-center justify-between mb-2"><h4 className="font-heading text-xl">{t.name}</h4>
              <div className="flex gap-1"><Button variant="ghost" size="icon" onClick={() => openEdit(t)}><Pencil className="h-4 w-4" /></Button>
                <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                  <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete {t.name}?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(t)} className="bg-destructive">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
              </div>
            </div>
            <pre className="text-xs text-muted-foreground whitespace-pre-line font-sans line-clamp-6">{t.body}</pre>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit" : "Add"} Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="tpl-name" /></div>
            <div><Label>Body</Label><Textarea rows={10} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} className="font-mono text-xs" data-testid="tpl-body" /></div>
            <p className="text-xs text-muted-foreground">Variables: [Customer Name] [Trip Name] [Departure Date] [Booking ID] [Pickup Point] [Number of Travellers] [Amount Paid]</p>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-template">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
